<script setup lang="ts">
import { onBeforeUnmount, watch } from 'vue';
import { X } from 'lucide-vue-next';

const props = withDefaults(defineProps<{ open: boolean; title: string; description?: string; size?: 'sm' | 'md' | 'lg' | 'xl'; side?: boolean }>(), {
  size: 'md',
  side: false,
});
const emit = defineEmits<{ close: [] }>();

function onKey(e: KeyboardEvent) {
  if (e.key === 'Escape' && props.open) emit('close');
}
watch(
  () => props.open,
  (open) => {
    document.body.style.overflow = open ? 'hidden' : '';
    if (open) window.addEventListener('keydown', onKey);
    else window.removeEventListener('keydown', onKey);
  },
  { immediate: true },
);
onBeforeUnmount(() => {
  window.removeEventListener('keydown', onKey);
  document.body.style.overflow = '';
});

const widths = { sm: 'max-w-md', md: 'max-w-xl', lg: 'max-w-3xl', xl: 'max-w-5xl' };
</script>

<template>
  <Teleport to="body">
    <Transition enter-from-class="opacity-0" leave-to-class="opacity-0" enter-active-class="transition" leave-active-class="transition">
      <div v-if="open" class="fixed inset-0 z-50 flex" :class="side ? 'justify-end' : 'items-end justify-center sm:items-center sm:p-4'" role="dialog" aria-modal="true" :aria-label="title">
        <div class="absolute inset-0 bg-navy-950/50 backdrop-blur-[1px]" @click="emit('close')" />
        <div
          class="surface relative flex max-h-full w-full flex-col border shadow-pop"
          :class="[widths[size], side ? 'h-full' : 'max-h-[92vh] rounded-t-2xl sm:rounded-2xl']"
        >
          <header class="flex items-start justify-between gap-4 border-b border-base px-5 py-4">
            <div>
              <h2 class="text-base font-semibold">{{ title }}</h2>
              <p v-if="description" class="mt-0.5 text-xs text-muted">{{ description }}</p>
            </div>
            <button type="button" class="rounded-md p-1 text-muted hover:bg-slate-100 dark:hover:bg-white/10" aria-label="Close" @click="emit('close')">
              <X class="h-5 w-5" />
            </button>
          </header>
          <div class="flex-1 overflow-y-auto px-5 py-4">
            <slot />
          </div>
          <footer v-if="$slots.footer" class="flex flex-wrap justify-end gap-2 border-t border-base px-5 py-3">
            <slot name="footer" />
          </footer>
        </div>
      </div>
    </Transition>
  </Teleport>
</template>
